import { css } from '@emotion/css';
import { useState } from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { useStyles2 } from '@grafana/ui';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { SavedQueriesDetailsPanel } from './SavedQueriesDetailsPanel';

type Props = {
  selectedQuery: SavedQuery | undefined;
  onTitleChange?: (title: string | undefined) => void;
  onDelete?: () => void;
  onSaveNew: (uid: string) => void;
};

// Right-side detail area: renders the full details panel when a query is selected, or nothing otherwise.
export function SavedQueriesDetails({ selectedQuery, onTitleChange, onDelete, onSaveNew }: Props) {
  const styles = useStyles2(getStyles);
  const [isDuplicate, setIsDuplicate] = useState(false);

  return (
    <div className={styles.container}>
      {selectedQuery && (
        <div className={styles.panel}>
          <SavedQueriesDetailsPanel
            key={selectedQuery.uid ?? 'new-query'}
            query={selectedQuery}
            onTitleChange={onTitleChange}
            onDelete={onDelete}
            isDuplicate={isDuplicate}
            onDuplicate={() => setIsDuplicate(true)}
            onCancelNew={() => setIsDuplicate(false)}
            onSaveNew={(uid) => {
              setIsDuplicate(false);
              onSaveNew(uid);
            }}
          />
        </div>
      )}
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => ({
  panel: css({
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.shape.radius.default,
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
  container: css({
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    [theme.breakpoints.down('lg')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
  }),
});
